// src/pages/JudoCoachPro/components/JudoCoachProPricing.jsx

import React, { useMemo, useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaChalkboardTeacher,
  FaExternalLinkAlt,
  FaHeart,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import {
  JUDO_COACH_COACH_PLANS,
  JUDO_COACH_PRICING_AUDIENCES,
  JUDO_COACH_PRO,
} from "../data/judoCoachProData";

const cx = (...classes) => classes.flat(Infinity).filter(Boolean).join(" ");

function AudienceSelector({ activeAudience, onChange }) {
  const audienceIcons = {
    judoka: FaUserGraduate,
    coach: FaChalkboardTeacher,
  };

  return (
    <div
      className="grid gap-3 sm:grid-cols-2"
      role="tablist"
      aria-label="Choisir une formule Judo Coach Pro"
    >
      {JUDO_COACH_PRICING_AUDIENCES.map((audience) => {
        const Icon = audienceIcons[audience.id];
        const active = activeAudience === audience.id;

        return (
          <button
            key={audience.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(audience.id)}
            className={cx(
              "group relative rounded-[20px] border p-5 text-left",
              "transition-all duration-300",
              active
                ? [
                    "border-[#0868f2] bg-blue-50/70",
                    "shadow-[0_20px_55px_-38px_rgba(8,104,242,0.7)]",
                    "dark:border-blue-400 dark:bg-blue-500/10",
                  ]
                : [
                    "border-slate-200 bg-white",
                    "hover:-translate-y-0.5 hover:border-[#0868f2]/35",
                    "dark:border-white/10 dark:bg-slate-900",
                  ],
            )}
          >
            <div className="flex items-start gap-4">
              <span
                className={cx(
                  "grid h-11 w-11 shrink-0 place-items-center rounded-full",
                  active
                    ? "bg-[#0868f2] text-white"
                    : "bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300",
                )}
              >
                <Icon />
              </span>

              <div className="min-w-0">
                <p
                  className={cx(
                    "text-[10px] font-semibold uppercase tracking-[0.18em]",
                    active
                      ? "text-[#ff7900]"
                      : "text-slate-400",
                  )}
                >
                  {audience.label}
                </p>

                <h3 className="mt-2 text-lg font-semibold text-slate-950 dark:text-white">
                  {audience.title}
                </h3>

                <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  {audience.short}
                </p>
              </div>
            </div>

            <span
              className={cx(
                "absolute right-4 top-4 h-2.5 w-2.5 rounded-full",
                active
                  ? "bg-[#ff7900]"
                  : "bg-slate-200 dark:bg-slate-700",
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

function FeatureList({ features }) {
  return (
    <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
      {features.map((feature) => (
        <div
          key={feature}
          className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
        >
          <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
            <FaCheck className="text-[8px]" />
          </span>

          <span>{feature}</span>
        </div>
      ))}
    </div>
  );
}

function CoachPlanSelector({
  activePlanId,
  onChange,
}) {
  return (
    <div className="border-b border-slate-200 dark:border-white/10">
      <div className="-mx-2 flex overflow-x-auto px-2">
        {JUDO_COACH_COACH_PLANS.map((plan) => {
          const active = plan.id === activePlanId;

          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onChange(plan.id)}
              className={cx(
                "group relative min-w-[160px] flex-1 px-4 pb-5 pt-2 text-left",
                "transition-colors",
                active
                  ? "text-[#0868f2] dark:text-blue-300"
                  : "text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white",
              )}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {plan.name}
                </span>

                {plan.recommended ? (
                  <span className="rounded-full bg-orange-50 px-2 py-1 text-[8px] font-semibold uppercase tracking-[0.12em] text-[#ff7900] dark:bg-orange-500/10">
                    Conseillé
                  </span>
                ) : null}
              </span>

              <span className="mt-1 block text-xs">
                {plan.priceText}
                {plan.suffix}
              </span>

              <span
                className={cx(
                  "absolute inset-x-4 -bottom-px h-[2px] bg-[#ff7900]",
                  "origin-left transition-transform duration-300",
                  active
                    ? "scale-x-100"
                    : "scale-x-0 group-hover:scale-x-100",
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function JudokaPricingContent() {
  const audience = JUDO_COACH_PRICING_AUDIENCES.find(
    (item) => item.id === "judoka",
  );

  return (
    <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ff7900]">
          {audience.eyebrow}
        </p>

        <h3 className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-slate-950 dark:text-white sm:text-4xl">
          {audience.title}
        </h3>

        <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
          {audience.description}
        </p>

        <div className="mt-7 inline-flex items-end gap-2">
          <span className="text-3xl font-semibold tracking-[-0.05em] text-[#0868f2] dark:text-blue-300 pr-6">
            {audience.priceText}
          </span>
        </div>

        <a
          href={JUDO_COACH_PRO.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-8 inline-flex h-12 items-center justify-center gap-3 rounded-full bg-[#0868f2] px-6 text-sm font-semibold text-white transition hover:bg-[#0648b8] pl-6"
        >
          {audience.callToAction}

          <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900]">
            <FaExternalLinkAlt className="text-[9px]" />
          </span>
        </a>
      </div>

      <div className="border-t border-slate-200 pt-7 dark:border-white/10 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
        <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          Tout ce qui est inclus
        </p>

        <FeatureList features={audience.features} />
      </div>
    </div>
  );
}

function CoachPricingContent() {
  const [activePlanId, setActivePlanId] = useState("coach-pro");

  const activePlan = useMemo(() => {
    return (
      JUDO_COACH_COACH_PLANS.find(
        (plan) => plan.id === activePlanId,
      ) || JUDO_COACH_COACH_PLANS[0]
    );
  }, [activePlanId]);

  return (
    <div>
      <CoachPlanSelector
        activePlanId={activePlanId}
        onChange={setActivePlanId}
      />

      <div
        key={activePlan.id}
        className="mt-9 grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-start"
      >
        <div>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
              <FaUsers />
            </span>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#ff7900]">
                Formule sélectionnée
              </p>

              <h3 className="mt-1 text-3xl font-semibold tracking-[-0.035em] text-slate-950 dark:text-white">
                {activePlan.name}
              </h3>
            </div>
          </div>

          <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-300">
            {activePlan.description}
          </p>

          <div className="mt-7 flex items-end gap-2">
            <span className="text-5xl font-semibold tracking-[-0.05em] text-slate-950 dark:text-white">
              {activePlan.priceText}
            </span>

            <span className="pb-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
              {activePlan.suffix}
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <span className="rounded-full bg-blue-50 px-3 py-2 text-xs font-semibold text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
              {activePlan.groups}
            </span>

            <span className="rounded-full bg-orange-50 px-3 py-2 text-xs font-semibold text-[#ff7900] dark:bg-orange-500/10">
              {activePlan.capacity}
            </span>
          </div>

          <a
            href={JUDO_COACH_PRO.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex h-12 items-center justify-center gap-3 rounded-full bg-[#0868f2] px-6 text-sm font-semibold text-white transition hover:bg-[#0648b8]"
          >
            Choisir cette formule

            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900]">
              <FaExternalLinkAlt className="text-[9px]" />
            </span>
          </a>
        </div>

        <div className="border-t border-slate-200 pt-7 dark:border-white/10 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
          <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Fonctionnalités comprises
          </p>

          <FeatureList features={activePlan.features} />
        </div>
      </div>
    </div>
  );
}

export default function JudoCoachProPricing() {
  const [activeAudience, setActiveAudience] = useState("judoka");

  return (
    <section
      id="jcp-pricing"
      className="scroll-mt-24 bg-white py-20 dark:bg-slate-950 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-8 border-b border-slate-200 pb-10 dark:border-white/10 lg:grid-cols-[1fr_0.85fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                Formules Judo Coach Pro
              </p>

              <h2 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Une formule adaptée
                <span className="block text-[#0868f2] dark:text-blue-300">
                  à votre pratique.
                </span>
              </h2>
            </div>

            <p className="max-w-xl text-base leading-8 text-slate-600 dark:text-slate-300 lg:justify-self-end">
              L’accès individuel reste gratuit pour les judokas. Les enseignants
              choisissent uniquement la capacité correspondant à leurs groupes.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <AudienceSelector
            activeAudience={activeAudience}
            onChange={setActiveAudience}
          />
        </Reveal>

        <Reveal className="mt-10">
          <div className="rounded-[26px] border border-slate-200 bg-[#f7f9f8] p-6 dark:border-white/10 dark:bg-slate-900 sm:p-8 lg:p-10">
            {activeAudience === "judoka" ? (
              <JudokaPricingContent />
            ) : (
              <CoachPricingContent />
            )}
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <div className="flex flex-col gap-6 border-y border-slate-200 py-7 dark:border-white/10 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                <FaHeart />
              </span>

              <div>
                <h3 className="text-base font-semibold text-slate-950 dark:text-white">
                  Soutenir le développement de l’application
                </h3>

                <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Les judokas peuvent utiliser gratuitement Judo Coach Pro. Un
                  don facultatif permet de soutenir son amélioration et le
                  développement de nouvelles fonctionnalités.
                </p>
              </div>
            </div>

            <a
              href={JUDO_COACH_PRO.donationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex h-11 shrink-0 items-center justify-center gap-3 self-start rounded-full border border-slate-300 px-5 text-sm font-semibold text-slate-800 transition hover:border-[#ff7900] hover:text-[#ff7900] dark:border-white/15 dark:text-white lg:self-auto"
            >
              Faire un don de 5 €

              <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}